import {
  ChartAPIFunctionProps,
  CreateChartOptionsWithColors,
} from '~/types/create.types';
import { TsDataPoint } from '~/types/timeseries.types';

export const drawYAxis = ({ options, context, y }: ChartAPIFunctionProps) => {
  drawAxisTicks(context, y.ticks, y.yScale, options);
  drawAxisBorder(context, options);
};

export const defaultAxisValues = (options: CreateChartOptionsWithColors) => {
  const textToBorderGap = 8;
  const isLeft = options.config?.yAxisPosition === 'left';
  const tickX = isLeft ? options.gutters.y : options.width - options.gutters.y;
  const textX = isLeft ? tickX - textToBorderGap : tickX + textToBorderGap;
  const maxCoord = options.height - options.gutters.x + 1;

  return { textToBorderGap, isLeft, tickX, textX, minCoord: 0, maxCoord };
};

interface DrawAxisTicksProps {
  context: CanvasRenderingContext2D;
  data: TsDataPoint[];
  yScale: (value: number) => number;
  options: CreateChartOptionsWithColors;
  rectColor: string;
  textColor: string;
}

const drawAxisTicks = (
  context: CanvasRenderingContext2D,
  data: number[],
  yScale: (value: number) => number,
  options: CreateChartOptionsWithColors
) => {
  const defaultValues = defaultAxisValues(options);

  for (const mark of data) {
    const yCoord = yScale(mark);
    if (yCoord < defaultValues.minCoord || yCoord > defaultValues.maxCoord) {
      continue;
    }
    drawGridLine(context, options, defaultValues.isLeft, yCoord);
    context.fillStyle = options.colors.typography.soft;
    drawText({
      context,
      isLeft: defaultValues.isLeft,
      label: mark.toString(),
      yCoord,
      textX: defaultValues.textX,
    });
  }
};

export const drawYAxisMarks = ({
  context,
  data,
  yScale,
  options,
  rectColor,
  textColor,
}: DrawAxisTicksProps) => {
  const defaultValues = defaultAxisValues(options);

  for (const mark of data) {
    const label = mark.value.toString();
    const yCoord = yScale(mark.value);
    if (yCoord < defaultValues.minCoord || yCoord > defaultValues.maxCoord) {
      continue;
    }
    const textSize = context.measureText(label);
    const rectHeight =
      textSize.fontBoundingBoxAscent + textSize.fontBoundingBoxDescent + 4;
    const centeredRect = yCoord - rectHeight / 2;
    context.fillStyle = rectColor;
    context.fillRect(
      defaultValues.isLeft ? 0 : defaultValues.tickX,
      centeredRect,
      options.gutters.y,
      rectHeight
    );

    context.fillStyle = textColor;
    drawText({
      context,
      isLeft: defaultValues.isLeft,
      label,
      yCoord,
      textX: defaultValues.textX,
    });
  }
};

interface DrawTextProps {
  context: CanvasRenderingContext2D;
  isLeft: boolean;
  label: string;
  yCoord: number;
  textX: number;
}

const drawText = ({ context, isLeft, label, yCoord, textX }: DrawTextProps) => {
  context.textBaseline = 'middle';
  context.textAlign = isLeft ? 'right' : 'left';
  context.fillText(label, textX, yCoord);
};

const drawAxisBorder = (
  context: CanvasRenderingContext2D,
  options: CreateChartOptionsWithColors
) => {
  context.fillStyle = options.colors.border.default;
  context.fillRect(
    options.config?.yAxisPosition === 'left'
      ? options.gutters.y
      : options.width - options.gutters.y,
    0,
    1,
    options.height - options.gutters.x + 1
  );
};

const drawGridLine = (
  context: CanvasRenderingContext2D,
  options: CreateChartOptionsWithColors,
  isLeft: boolean,
  yCoord: number
) => {
  context.fillStyle = options.colors.border.soft;
  context.fillRect(
    isLeft ? options.gutters.y : 0,
    yCoord,
    options.width - options.gutters.y,
    1
  );
};
